import React, { useRef } from 'react';
import PlayerControlsScrubberBase from './player-controls-scrubber-base';
import { usePlayerContext } from '../../_contexts/player-context';
import { PlayerEvent, SeekableRange } from '../../types';
import usePlayerEvent from '../../_hooks/use-player-event';
import { useAtom, useSetAtom } from 'jotai';
import {
  isSkippingAtom,
  isSkipButtonInProgressAtom,
  validatedTargetPositionAtom
} from '../player-controls-navigation/skip-buttons';
import {
  getPosition,
  getProgressPercentage,
  hasProgramRelativePosition
} from './player-controls-scrubber-utils';

export const PlayerControlsScrubberVod: React.FC = () => {
  const [duration, setDuration] = React.useState(0);

  const [progressSeconds, setProgressSeconds] = React.useState(0);
  const [progressPercentage, setProgressPercentage] = React.useState(0);

  const [scrubbing, setScrubbing] = React.useState(false);

  const { player, setIsPlaying } = usePlayerContext();

  const [isSkipping, setIsSkipping] = useAtom(isSkippingAtom);
  const setValidatedTargetPosition = useSetAtom(validatedTargetPositionAtom);
  const isSkipButtonInProgress = useAtom(isSkipButtonInProgressAtom);

  const timeoutSkipIdRef = useRef<NodeJS.Timeout>();

  const handlePlayerOpened = React.useCallback(() => {
    const duration = player?.getDuration() || 0;

    setDuration(duration);
  }, [player]);

  const handlePlaytimeUpdated = React.useCallback(
    (seekableRange: SeekableRange) => {
      if (scrubbing || !hasProgramRelativePosition(seekableRange)) return;

      const { position, duration } = seekableRange;

      if (isSkipButtonInProgress[0]) {
        clearTimeout(timeoutSkipIdRef.current);
      }

      setDuration(duration);
      setProgressSeconds(Math.round(position));
      setProgressPercentage(getProgressPercentage(position, duration));

      if (isSkipping) {
        timeoutSkipIdRef.current = setTimeout(() => {
          // If the user is not pushing the skip button, we can reset the skip state
          if (!isSkipButtonInProgress[0]) {
            setIsSkipping(false);
            setValidatedTargetPosition(0);
          }
        }, 250);
      }
    },
    [isSkipping, isSkipButtonInProgress, scrubbing, setIsSkipping, setValidatedTargetPosition]
  );

  const handleChange = React.useCallback((value: number) => {
    setProgressPercentage(value);

    setScrubbing(true);
  }, []);

  const handleChangeCommitted = React.useCallback(
    (value: number) => {
      setScrubbing(false);

      const position = getPosition(value, duration);

      player?.setPosition(position);

      player?.play();
      setIsPlaying(true);
    },
    [duration, player, setIsPlaying]
  );

  usePlayerEvent(PlayerEvent.Opened, handlePlayerOpened);
  usePlayerEvent(PlayerEvent.PlayTimeUpdated, handlePlaytimeUpdated);

  const progress = React.useMemo(
    () => ({ duration, progressSeconds, progressPercentage }),
    [duration, progressSeconds, progressPercentage]
  );

  return (
    <PlayerControlsScrubberBase
      progress={progress}
      onChange={handleChange}
      onChangeCommitted={handleChangeCommitted}
    />
  );
};

export default React.memo(PlayerControlsScrubberVod);
